import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRightIcon, ArrowUpRightIcon } from "lucide-react";
import { currentWork, type CurrentWorkItem } from "../data/currentWork";
import { SectionHeading } from "./SectionHeading";
import { EYEBROW, H3 } from "../utils/typography";
const ease = [0.23, 1, 0.32, 1] as const;
export function CurrentlyWorkingOn() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, {
    margin: "-10% 0px"
  });
  const animate = !reduce && inView;
  return <section ref={ref} id="now" aria-labelledby="now-heading" className="w-full scroll-mt-28 bg-cream px-6 pb-24 md:scroll-mt-36 md:px-12 md:pb-32 lg:px-20 lg:pb-40" data-id="element-209" data-mp="2srq61tqhidyj">
      <SectionHeading id="now-heading" title="NOW" data-id="element-210" data-mp="1hckq3o1ceiwz" />

      <p className={`mt-24 md:mt-32 lg:mt-40 ${EYEBROW} flex items-center gap-3 text-rust`} data-id="element-211" data-mp="1hbrp73p2enmr">
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true" data-id="element-212" data-mp="12tdx7rtf9bga">
          {!reduce && <motion.span className="absolute inline-flex h-full w-full rounded-full bg-mustard" animate={{
          scale: [1, 1.8],
          opacity: [0.35, 0]
        }} transition={{
          duration: 2.4,
          ease: "easeOut",
          repeat: Infinity,
          repeatDelay: 1.2
        }} data-id="element-213" data-mp="17pulcm0mfptd" />}
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-mustard" data-id="element-214" data-mp="k0t6afxa9gf4" />
        </span>
        Currently working on
      </p>

      <ul className="mt-6 grid grid-cols-1 gap-6 md:mt-8 md:grid-cols-2" data-id="element-215" data-mp="ou4het94ojcu">
        {currentWork.map((item, i) => <motion.li key={item.id} className="flex" initial={reduce ? false : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.25
      }} transition={{
        duration: 0.3,
        ease,
        delay: i % 2 * 0.06
      }} data-id="element-216" data-mp="f28hrdre8l6i" data-mpi={i}>
            <WorkItem item={item} animate={animate} offset={i * 0.9} data-id="element-217" data-mp="27r94dqsw4ir0" />
          </motion.li>)}
      </ul>

      <a href="#contact" className="group mt-8 inline-flex min-h-12 items-center gap-3 rounded-full bg-dusty py-2 pl-6 pr-2 text-sm font-medium uppercase tracking-wide text-cream transition-colors duration-150 ease-out hover:bg-espresso focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dusty focus-visible:ring-offset-4 focus-visible:ring-offset-cream" data-id="element-218" data-mp="22z7uucn3x64o">
        Curious about any of these? Ask me
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream text-dusty transition-colors duration-150 ease-out group-hover:text-espresso" data-id="element-219" data-mp="2lonwj3aj34mo">
          <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-220" data-mp="2cpkdfzbd2p86" />
        </span>
      </a>
    </section>;
}
function WorkItem({
  item,
  animate,
  offset
}: {
  item: CurrentWorkItem;
  animate: boolean;
  offset: number;
}) {
  const linked = Boolean(item.href);
  const card = <article className={`flex h-full w-full flex-col rounded-3xl bg-paper p-6 md:p-8 ${linked ? "transition-colors duration-150 ease-out group-hover:bg-[#e9e0cf]" : ""}`} data-id="element-221" data-mp="3hyewiig7z3kq">
      <div className="flex items-start justify-between gap-4" data-id="element-222" data-mp="3qqp5xxjaj50x">
        <p className={`${EYEBROW} text-espresso/70`} data-id="element-223" data-mp="3voqn2cx5ou9r">
          {item.org}
          {item.role && <>
              {" "}
              <span aria-hidden="true" data-id="element-224" data-mp="4h5ts87z5fjp">·</span> {item.role}
            </>}
        </p>
        {linked && <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rust text-cream transition-colors duration-150 ease-out group-hover:bg-espresso" data-id="element-225" data-mp="9ibno9ddf8ut">
            <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-226" data-mp="2faifboh1opi6" />
          </span>}
      </div>
      <h3 className={`mt-3 ${H3} text-espresso`} data-id="element-227" data-mp="1k26fsr34k9go">{item.title}</h3>
      <p className="mt-3 max-w-md text-base leading-relaxed text-espresso/80" data-id="element-228" data-mp="1fxyruw4hqxji">{item.summary}</p>

      <div className="mt-auto pt-8" data-id="element-229" data-mp="1tx6u5xvyalrx">
        <p className={`${EYEBROW} text-espresso/70`} data-id="element-230" data-mp="1tw5eth7wjd4e">{item.label}</p>
        <div className="mt-3" data-id="element-231" data-mp="1ozy6jhs19pja">
          {item.visual === "questions" && <RotatingQuestion items={item.items} animate={animate} offset={offset} data-id="element-232" data-mp="1192k706cjmjf" />}
          {item.visual === "cycle" && <SprintCycle items={item.items} animate={animate} offset={offset} data-id="element-233" data-mp="wbsk8qlbzshc" />}
          {item.visual === "flow" && <DataFlow items={item.items} animate={animate} data-id="element-234" data-mp="1b0fcfsw24epz" />}
          {item.visual === "assemble" && <Assemble items={item.items} animate={animate} offset={offset} data-id="element-235" data-mp="167vygutgjhkn" />}
        </div>
      </div>
      {linked && <span className="sr-only" data-id="element-236" data-mp="ii1m0ed1vv0x">Read the case study</span>}
    </article>;
  if (!item.href) return card;
  return <Link to={item.href} className="group flex w-full rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-4 focus-visible:ring-offset-cream" data-id="element-237" data-mp="djzur65h0ped">
      {card}
    </Link>;
}

/** Cycles index every `interval` ms while animating; starts after `offset` seconds. */
function useCycle(length: number, animate: boolean, offset: number, interval: number) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let timer: number | undefined;
    const start = window.setTimeout(() => {
      timer = window.setInterval(() => setIndex(i => (i + 1) % length), interval);
    }, offset * 1000);
    return () => {
      window.clearTimeout(start);
      if (timer) window.clearInterval(timer);
    };
  }, [animate, length, offset, interval]);
  return index;
}
function RotatingQuestion({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  const index = useCycle(items.length, animate, offset, 5200);
  return <div className="rounded-2xl bg-espresso px-5 py-4" data-id="element-238" data-mp="3u71m9y7x291g">
      {/* Screen readers get the full list once, not a moving target */}
      <ul className="sr-only" data-id="element-239" data-mp="3p7ypf123b94k">
        {items.map(q => <li key={q} data-id="element-240" data-mp="3kg6vcgjq6l9o">{q}</li>)}
      </ul>
      <div aria-hidden="true" className="relative h-14 overflow-hidden" data-id="element-241" data-mp="3kevuoplkttms">
        <AnimatePresence initial={false} mode="popLayout" data-id="element-242" data-mp="3ge22hk13pso3">
          <motion.p key={items[index]} className="absolute inset-x-0 top-0 text-base font-medium leading-snug text-cream" initial={{
          y: 8,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -8,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease
        }} data-id="element-243" data-mp="3bfqvdh26xw9k">
            {items[index]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>;
}
function SprintCycle({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  const index = useCycle(items.length, animate, offset, 3000);
  const layoutId = useId();
  return <ol className="grid grid-cols-2 gap-2" data-id="element-244" data-mp="2dlpy4987hpyl">
      {items.map((step, i) => {
      const active = animate && i === index;
      return <li key={step} className="relative" data-id="element-245" data-mp="2ncas1jcxq6b6" data-mpi={i}>
            {active && <motion.span layoutId={layoutId} aria-hidden="true" className="absolute inset-0 rounded-full bg-espresso" transition={{
          duration: 0.25,
          ease
        }} data-id="element-246" data-mp="2nd40corjl3vf" />}
            <span className={`relative flex h-10 items-center justify-center rounded-full text-sm font-medium uppercase tracking-wide ring-1 transition-colors duration-200 ease-out ${active ? "text-cream ring-transparent" : "text-espresso ring-espresso/20"}`} data-id="element-247" data-mp="1zm85tjvpbukh">
              {step}
            </span>
          </li>;
    })}
    </ol>;
}

/** Reusable sections settle into place one by one, like a page being composed from existing parts. */
function Assemble({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  // Headline, image, copy carry from the printed piece onto the screen, then a beat with all in place
  const step = useCycle(4, animate, offset, 1800);
  const shown = (i: number) => !animate || i < step ? "opacity-100" : "opacity-20";
  const fade = "transition-opacity duration-300 ease-out";
  const [printLabel, webLabel] = items;
  return <div className="flex items-end gap-3" data-id="element-248" data-mp="3eibyrkab74fv">
      <p className="sr-only" data-id="element-249" data-mp="39i7jtyp1uhrr">The printed campaign pieces carried onto the website.</p>

      {/* Printed piece: portrait sheet, always complete */}
      <figure aria-hidden="true" className="shrink-0" data-id="element-250" data-mp="3nf312efgffiw">
        <div className="flex h-[5.5rem] w-16 flex-col gap-1.5 rounded-md bg-cream p-2 shadow-[0_6px_14px_-8px_rgba(29,24,20,0.35)] ring-1 ring-espresso/10" data-id="element-251" data-mp="3jgnak0k5zp0q">
          <span className="h-1.5 w-3/4 rounded-full bg-mustard" data-id="element-252" data-mp="1p1ko6bqewn21" />
          <span className="h-7 rounded-sm bg-espresso/80" data-id="element-253" data-mp="1p3dvlt8unbb3" />
          <span className="h-1 w-full rounded-full bg-espresso/25" data-id="element-254" data-mp="1exdzh99prj5i" />
          <span className="h-1 w-2/3 rounded-full bg-espresso/25" data-id="element-255" data-mp="1iz7xtawfsb2i" />
        </div>
        <figcaption className="mt-2 text-xs font-medium uppercase tracking-wide text-espresso/60" data-id="element-256" data-mp="1j1byljz1gtyk">{printLabel}</figcaption>
      </figure>

      <ArrowRightIcon aria-hidden="true" className="mb-12 h-4 w-4 shrink-0 text-espresso/35" data-id="element-257" data-mp="1a6e2y54ib33j" />

      {/* Screen: the same pieces settle in one at a time */}
      <figure aria-hidden="true" className="min-w-0 flex-1" data-id="element-258" data-mp="vbrg126srypt">
        <div className="flex h-[5.5rem] flex-col overflow-hidden rounded-lg bg-cream shadow-[0_6px_14px_-8px_rgba(29,24,20,0.35)] ring-1 ring-espresso/10" data-id="element-259" data-mp="109swdfcu9jxl">
          <div className="flex gap-1 border-b border-espresso/10 px-2 py-1.5" data-id="element-260" data-mp="r81u6khibv8h">
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-261" data-mp="ra5vcuo0ug52" />
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-262" data-mp="2goazk6wrko29" />
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-263" data-mp="2lmm6o9vockgs" />
          </div>
          <div className="grid flex-1 grid-cols-[minmax(0,1fr)_40%] gap-2 p-2" data-id="element-264" data-mp="efyq4r4gvtny">
            <div className="flex flex-col gap-1.5" data-id="element-265" data-mp="edvtvpcq14as">
              <span className={`h-1.5 w-3/4 rounded-full bg-mustard ${fade} ${shown(0)}`} data-id="element-266" data-mp="j7729ux6lt4t" />
              <span className={`flex flex-col gap-1.5 pt-1 ${fade} ${shown(2)}`} data-id="element-267" data-mp="5g1w09zb62li">
                <span className="h-1 w-full rounded-full bg-espresso/25" data-id="element-268" data-mp="9fu8uyvqiuwo" />
                <span className="h-1 w-5/6 rounded-full bg-espresso/25" data-id="element-269" data-mp="x66neslhjpw8" />
                <span className="h-1 w-2/3 rounded-full bg-espresso/25" data-id="element-270" data-mp="x43pzni06uhh" />
              </span>
            </div>
            <span className={`rounded-sm bg-espresso/80 ${fade} ${shown(1)}`} data-id="element-271" data-mp="122x5tmkrixkt" />
          </div>
        </div>
        <figcaption className="mt-2 text-xs font-medium uppercase tracking-wide text-espresso/60" data-id="element-272" data-mp="t4l5uty0anfp">{webLabel}</figcaption>
      </figure>
    </div>;
}

/** Three nodes joined by tracks; a mustard pulse carries data from GA4 through the MCP to Claude. */
function DataFlow({
  items,
  animate
}: {
  items: string[];
  animate: boolean;
}) {
  return <div className="flex h-[5.5rem] items-center rounded-2xl bg-espresso px-4" data-id="element-273" data-mp="35cs4pglal1ii">
      <p className="sr-only" data-id="element-274" data-mp="31crgj48e5by9">{items.join(" connects through ")}</p>
      <div aria-hidden="true" className="flex w-full items-center" data-id="element-275" data-mp="31ekiw7bz1a0g">
        {items.map((node, i) => <React.Fragment key={node} data-id="element-276" data-mp="3acwkt5iafu87" data-mpi={i}>
            <span className={`flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-3 text-sm sm:px-4 font-medium uppercase tracking-wide ${i === 1 ? "bg-mustard text-espresso" : "text-cream ring-1 ring-cream/25"}`} data-id="element-277" data-mp="3adxwnc74okqz">
              {node}
            </span>
            {i < items.length - 1 && <span className="relative mx-2 h-1.5 flex-1 overflow-hidden" data-id="element-278" data-mp="2rlwt3wjes6b8">
                <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-cream/20" data-id="element-279" data-mp="23wuaald1g7f5" />
                {animate && <motion.span className="absolute top-1/2 h-1 w-4 -translate-y-1/2 rounded-full bg-mustard/70" initial={{
            left: "-25%"
          }} animate={{
            left: "110%"
          }} transition={{
            duration: 1.6,
            ease: "linear",
            repeat: Infinity,
            repeatDelay: 2.4,
            delay: i * 1.6
          }} data-id="element-280" data-mp="1yyj0gamqtwwx" />}
              </span>}
          </React.Fragment>)}
      </div>
    </div>;
}